import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { NavHashLink } from 'react-router-hash-link';
import { firestore } from './FirebaseConfig';
import { Stack, Avatar, Typography, Grid, Card, CircularProgress, Badge, Divider, Chip } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import BlogTitle from './BlogTitle';
import Paragraph from './Paragraph';
import Moreposts from './Moreposts';
import CardLoader from './CardLoader';

import { getLikesCount, getComments } from './Likes';

const Blogpost = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageSrc, setImageSrc] = useState('');
  const [avatar, setAvatar] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [authorId, setAuthorId] = useState('');
  const [likesCount, setLikesCount] = useState(0);
  const [date, setDate] = useState(0);
  const [processedComments, setComments] = useState([]);
  const history = useNavigate();
  const slug = id;

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const postRef = firestore.collection('teamPosts').doc(id);
        const doc = await postRef.get();

        if (!doc.exists) {
          console.log('No matching document.');
          return;
        }

        const postData = doc.data();
        
        setTitle(postData.title);
        setContent(postData.content);
        setAvatar(postData.avatar);
        setDisplayName(postData.displayName);
        setComments(postData.comments);
        setDate(postData.date);
        setAuthorId(postData.userId);
      
        const firstMediaItem = postData.media?.[0];
        if (firstMediaItem && firstMediaItem.src) {
          setImageSrc(firstMediaItem.src);
        }

        setTimeout(() => {
          setLoading(false);
        }, 2000);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      }
    };

    fetchPost();
  }, [id]);

  const handleArticleClick = (newId) => {       
    history.push(`/blog/${newId}`); // Navigate to the new article route
  };

  

  useEffect(() => {
    getLikesCount(slug, setLikesCount);
    getComments(slug, setComments);
    
  }, [slug]);

  // Convert and format the date
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  // Generate JSON-LD markup for rich snippets
  const richSnippetMarkup = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    image: [imageSrc],
    datePublished: formatDate(date), // Convert and format the published date
    dateModified: formatDate(date), // Convert and format the modified date
    author: [
      {
        '@type': 'Person',
        name: displayName,
        url: `https://basement.egresswindows.app/profile-page/${authorId}`, 
      },
    ],
  });

  return (

    
    <Stack
      id='article'
      component='section'
      direction='column'
      minWidth='300px'
      width='90%'
      paddingTop='200px'
      paddingBottom='100px'
      justifyContent='center'
      alignItems='center'
      marginBottom={'20px'}
      marginTop={'200px'}
      sx={{
        py: '100px',
        mx: 6,
        maxWidth: '800px',
        margin: '0px auto',
        opacity: loading ? 0.5 : 1,
        transition: 'opacity 2s ease-in-out',
      }}
    >
      <Helmet>
        
        <script type='application/ld+json'>{richSnippetMarkup}</script>
      </Helmet>
      {loading ? (
        <CardLoader/>
       
      ) : (
        <>
          <BlogTitle text={title} fontFamily='League Spartan sans-serif' textAlign='left' sx={{color: '#000000'}} />

          {imageSrc && (
            <img
              src={imageSrc}
              alt={title}
              style={{
                width: '100%',
                maxWidth: '800px',
                height: 'auto',
                borderRadius: '10px',
                marginTop: '40px',
                marginBottom: '40px',
              }}
            />
          )}

             <Grid
                 item
                 sx={{
                   width: '90%',
                   display: 'flex',
                   flexDirection: 'row',
                   flexWrap: 'wrap',
                   justifyItems: 'baseline',
                   alignItems: 'center',
                   verticalAlign: 'middle',
                 }}
             >
          
             
              
             </Grid>
        
          <Paragraph
            text={content}
            maxWidth='90vw'
            mx={0}
            textAlign='left'
            whiteSpace='pre-wrap'
            sx={{ color: '#121212', marginBottom: '40px', paddingBottom: '40px', whiteSpace: 'pre-wrap' }}
          />
          <Divider color="primary">
    
  </Divider>
          <Card
            style={{
              backgroundColor: 'rgba(0,0,0,0.14)',
              height: 'fit-content',
              justifyItems: 'baseline',
              width: '85vw',
              maxWidth: '800px',
              padding: '10px',
              paddingRight: '20px',
              marginBottom: '25px',
              marginTop: '25px',
              display: 'flex',
              msFlexDirection: 'row',
              borderRadius: '8px',
            }}
          >
            <Grid
              item
              sx={{
                width: '90%',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyItems: 'baseline',
                alignItems: 'center',
                verticalAlign: 'middle',
              }}
            >

<NavHashLink to={`/profile-page/${authorId}#profile`}>
              <Avatar
              
                src={avatar}
                alt={displayName}
                style={{ marginRight: '10px', width: '100px', height: '100px', alignSelf: 'baseline' }}
              />
              </NavHashLink>
              <Typography
                variant='h4'
                component='h4'
                sx={{
                  lineHeight: 1,
                  height: '30%',
                  linHeight: 0,
                  color: '#121212',
                  textAlign: 'center',
                  alignItems: 'center',

                  marginLeft: '20px',
                  verticalAlign: 'middle',
                  '@media (max-width:420px)': {
                    fontSize: '26px',

                    verticalAlign: 'middle',
                    marginLeft: '20px',
                  },
                }}
              >
                {displayName}
              </Typography>
             
            </Grid>
            <Grid sx={{height: '50%', textAlign: 'center', margin: '0px auto', lineHeight: '100%', marginTop: '20px'}}>
            
            <Badge color="primary" badgeContent={likesCount} >
             
            <FavoriteIcon sx={{color:'#f5f5f5', height: '2em', width: '2em'}}></FavoriteIcon>
            </Badge>

            </Grid>
            
          </Card>
          {processedComments}
          <div>
  
  
  
</div>

          <Moreposts sx={{opacity: loading ? 0 : 1}} onArticleClick={handleArticleClick} />
         
        </>
      )}
    </Stack>
  );
};

export default Blogpost;


